// src/pages/ArabicNumberSpeedGame.js
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import confetti from 'canvas-confetti';
import PropTypes from 'prop-types';
import useAudio from '../../hooks/useAudio';
import useNumbersGame from '../../hooks/useNumbersGame';
import useWebWorkerTimer from '../../hooks/useWebWorkerTimer';
import { Link } from 'react-router-dom';
import useAnalytics from '../../hooks/useAnalytics';

// Scoring constants
const SCORING_CONSTANTS = {
  BASE_POINTS: 15,
  STREAK_MULTIPLIER: 2,
  SPEED_BONUS_MAX: 100,
  ACCURACY_BONUS_MAX: 200,
  PERFECT_ACCURACY_BONUS: 300,
  LEVEL_COMPLETE_BONUS: 150,
  MAX_SPEED_BONUS_TIME: 3, // seconds
  MIN_ACCURACY_FOR_STARS: 70
};

// Level configurations
const LEVEL_CONFIGS = {
  1: { 
    range: [0, 10], 
    timeLimit: 60, 
    questionsCount: 15, 
    title: "Numbers 0-10",
    description: "Master the basics!",
    passingScore: 70
  },
  2: { 
    range: [0, 20], 
    timeLimit: 60, 
    questionsCount: 20, 
    title: "Numbers 0-20",
    description: "Building confidence!",
    passingScore: 75
  },
  3: { 
    range: [0, 50], 
    timeLimit: 90, 
    questionsCount: 25, 
    title: "Numbers 0-50",
    description: "Getting serious!",
    passingScore: 75
  },
  4: { 
    range: [0, 100], 
    timeLimit: 120, 
    questionsCount: 30, 
    title: "Numbers 0-100",
    description: "The hundreds challenge!",
    passingScore: 80
  },
  5: { 
    range: [0, 200], 
    timeLimit: 150, 
    questionsCount: 35, 
    title: "Numbers 0-200",
    description: "Master level!",
    passingScore: 80
  },
  6: { 
    range: [100, 200], 
    timeLimit: 120, 
    questionsCount: 30, 
    title: "Large Numbers",
    description: "Expert challenge!",
    passingScore: 85
  },
  7: { 
    range: [0, 200], 
    timeLimit: 180, 
    questionsCount: 50, 
    title: "Lightning Round",
    description: "Ultimate test!",
    passingScore: 85
  }
};

const ArabicNumberSpeedGame = () => {
  // Initialize hooks
  const { 
    userId,
    level,
    setLevel,
    maxLevel,
    numbersData,
    bestScores,
    isLoading,
    error,
    saveLevelData,
    updateLevel,
    unlockNextLevel,
    resetAllProgress
  } = useNumbersGame('arabicNumberSpeedGame');

  const {
    playCorrectSound,
    playIncorrectSound,
    playPerfectSound,
    playStreakSound,
    playWhooshSound,
    playStarSound,
    startBackgroundMusic,
    AudioElements
  } = useAudio();

  const timer = useWebWorkerTimer();

  const { saveSessionData } = useAnalytics();

  // Game active ref to track if game is running (independent of React state)
  const gameActiveRef = useRef(false);
  const audioRef = useRef(null);
  const audioTimeoutRef = useRef(null);

  // Game state - memoized to prevent unnecessary re-renders
  const [gamePhase, setGamePhase] = useState('menu'); // 'menu', 'playing', 'complete'
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [answerOptions, setAnswerOptions] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [sessionStartTime, setSessionStartTime] = useState(Date.now());
  const [sessionMistakes, setSessionMistakes] = useState([]);
  
  // Scoring state
  const [totalScore, setTotalScore] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [levelStartTime, setLevelStartTime] = useState(Date.now());
  const [speedBonuses, setSpeedBonuses] = useState(0);
  const [streakBonuses, setStreakBonuses] = useState(0);
  const [stars, setStars] = useState(0);
  
  // UI state
  const [feedback, setFeedback] = useState('');
  const [celebrations, setCelebrations] = useState(false);
  const [sparkles, setSparkles] = useState(false);
  const [showLevelComplete, setShowLevelComplete] = useState(false);
  const [levelCompleteData, setLevelCompleteData] = useState(null);
  const [currentTimeLeft, setCurrentTimeLeft] = useState(0);
  const [currentProgress, setCurrentProgress] = useState(100);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showScoringHelp, setShowScoringHelp] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);

  // Modal state
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const [showReplayConfirmation, setShowReplayConfirmation] = useState(false);
  const [replayLevel, setReplayLevel] = useState(null);

  // Get current level config - memoized
  const levelConfig = useMemo(() => LEVEL_CONFIGS[level] || LEVEL_CONFIGS[1], [level]);

  // Utility functions - memoized to prevent re-creation
  const shuffleArray = useCallback((array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  }, []);

  const getRandomNumber = useCallback(() => {
    const [min, max] = levelConfig.range;
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }, [levelConfig.range]);

  const generateAnswerOptions = useCallback((correctNumber) => {
    const options = [correctNumber];
    const [min, max] = levelConfig.range;
    
    while (options.length < 4) {
      const randomNum = Math.floor(Math.random() * (max - min + 1)) + min;
      if (!options.includes(randomNum)) {
        options.push(randomNum);
      }
    }
    
    return shuffleArray(options);
  }, [levelConfig.range, shuffleArray]);

  // Simplified and more reliable audio playback
  const playNumberAudio = useCallback(async (number) => {
    console.log('🔊 Playing audio for number:', number);
    
    // Clear any existing audio and timeouts
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = '';
      audioRef.current = null;
    }
    
    if (audioTimeoutRef.current) {
      clearTimeout(audioTimeoutRef.current);
      audioTimeoutRef.current = null;
    }

    const numberData = numbersData.find(n => n.englishNumeral === number);
    if (!numberData?.audioURL) {
      console.log(`No audio available for number ${number}`);
      return;
    }

    setAudioPlaying(true);

    try {
      const audio = new Audio();
      audioRef.current = audio;
      
      // Set up a safety timeout
      audioTimeoutRef.current = setTimeout(() => {
        console.log('🔊 Audio timeout - stopping playback');
        cleanupAudio();
      }, 5000);
      
      // Create cleanup function that removes listeners
      const cleanupAudio = () => {
        setAudioPlaying(false);
        if (audioTimeoutRef.current) {
          clearTimeout(audioTimeoutRef.current);
          audioTimeoutRef.current = null;
        }
        
        // Remove event listeners before cleanup
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('error', handleError);
        
        // Clear the audio
        try {
          audio.pause();
          audio.currentTime = 0;
          audio.src = '';
        } catch (e) {
          // Ignore cleanup errors
        }
        
        audioRef.current = null;
      };
      
      const handleEnded = () => {
        console.log('🔊 Audio ended normally');
        cleanupAudio();
      };
      
      const handleError = (error) => {
        console.warn('🔊 Audio error for number', number, ':', error);
        cleanupAudio();
      };
      
      // Add event listeners
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('error', handleError);
      
      audio.volume = 0.8;
      audio.src = numberData.audioURL;
      
      await audio.play();
      
    } catch (error) {
      console.error('🔊 Failed to play audio:', error);
      setAudioPlaying(false);
      if (audioTimeoutRef.current) {
        clearTimeout(audioTimeoutRef.current);
        audioTimeoutRef.current = null;
      }
    }
  }, [numbersData]);

  const lastCelebrationRef = useRef(0);
  const triggerCelebration = useCallback((isStreak = false, earnedStars = 0) => {
    // Throttle celebrations to prevent spam
    const now = Date.now();
    if (now - lastCelebrationRef.current < 500) { // Minimum 500ms between celebrations
      return;
    }
    lastCelebrationRef.current = now;
    
    setCelebrations(true);
    setSparkles(true);
    
    let colors = ['#FF6B6B', '#4ECDC4', '#45B7D1'];
    let particleCount = 50; // Significantly reduced
    let shapes = ['circle'];
    
    if (earnedStars > 0) {
      colors = ['#FFD700', '#FFA500'];
      shapes = ['star'];
      particleCount = 75; // Reduced
      playStarSound();
    } else if (isStreak) {
      colors = ['#FFD700', '#FFA500'];
      particleCount = 60; // Reduced
      playStreakSound();
    }
    
    // Reduced confetti parameters for better performance
    confetti({
      particleCount,
      spread: 50, // Reduced
      origin: { y: 0.7 },
      colors,
      shapes,
      gravity: 1.2, // Faster fall
      drift: 0
    });

    // Faster cleanup
    setTimeout(() => {
      setCelebrations(false);
      setSparkles(false);
    }, 1000); // Reduced from 2000
  }, [playStarSound, playStreakSound]);

      useEffect(() => {
      return () => {
        // Reset confetti on unmount
        confetti.reset();
      };
    }, []);

  const generateQuestion = useCallback(() => {
    const number = getRandomNumber();
    const numberData = numbersData.find(n => n.englishNumeral === number);
    
    if (!numberData) {
      console.error(`No data found for number ${number}`);
      return null;
    }

    const options = generateAnswerOptions(number);
    
    return {
      number,
      arabicNumeral: numberData.arabicNumeral,
      arabicWord: numberData.arabicWord,
      pronunciation: numberData.pronunciation,
      audioURL: numberData.audioURL,
      correctAnswer: number,
      options
    };
  }, [getRandomNumber, numbersData, generateAnswerOptions]);

  const calculateSpeedBonus = useCallback((responseTime) => {
    const maxTime = SCORING_CONSTANTS.MAX_SPEED_BONUS_TIME * 1000;
    if (responseTime <= maxTime) {
      const bonus = Math.round(SCORING_CONSTANTS.SPEED_BONUS_MAX * (1 - responseTime / maxTime));
      return Math.max(0, bonus);
    }
    return 0;
  }, []);

  const calculateStars = useCallback((accuracy, totalScore, questionsCount) => {
    const maxPossibleScore = questionsCount * (SCORING_CONSTANTS.BASE_POINTS + SCORING_CONSTANTS.SPEED_BONUS_MAX);
    const scorePercentage = (totalScore / maxPossibleScore) * 100;
    
    if (accuracy >= 95 && scorePercentage >= 85) return 3;
    if (accuracy >= 85 && scorePercentage >= 70) return 2;
    if (accuracy >= 70 && scorePercentage >= 50) return 1;
    return 0;
  }, []);

  const setupNewQuestion = useCallback(() => {
    const question = generateQuestion();
    if (!question) return false;
    
    setCurrentQuestion(question);
    setAnswerOptions(question.options);
    setSelectedAnswer(null);
    setIsAnswered(false);
    setFeedback('');
    setQuestionStartTime(Date.now());
    
    // Auto-play audio after a shorter delay
    setTimeout(() => {
      playNumberAudio(question.number);
    }, 500); // Reduced from 800
    
    return true;
  }, [generateQuestion, playNumberAudio]);

  // Enhanced Timer Display with kids-friendly design
  const BigTimer = React.memo(() => {
    const isLowTime = currentTimeLeft <= 10;
    const isCritical = currentTimeLeft <= 5;
    
    return (
      <div className="relative">
        <div className={`bg-gradient-to-r from-purple-500 to-pink-500 rounded-3xl p-4 md:p-6 shadow-2xl border-4 border-white ${
          isCritical ? 'animate-bounce' : isLowTime ? 'animate-pulse' : ''
        }`}>
          <div className="text-center">
            <div className="text-white text-sm md:text-lg font-bold mb-2">⏰ TIME LEFT</div>
            <div className={`text-4xl md:text-6xl font-black text-white mb-2 md:mb-3 ${
              isCritical ? 'text-red-300 animate-pulse' : isLowTime ? 'text-yellow-300' : ''
            }`}>
              {Math.ceil(currentTimeLeft)}
            </div>
            <div className="text-white text-xs md:text-sm font-bold">seconds</div>
            
            {/* Circular progress ring */}
            <div className="relative w-16 h-16 md:w-20 md:h-20 mx-auto mt-3 md:mt-4">
              <svg className="w-16 h-16 md:w-20 md:h-20 transform -rotate-90" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  stroke="rgba(255,255,255,0.3)"
                  strokeWidth="8"
                  fill="none"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  stroke={isCritical ? "#ff4444" : isLowTime ? "#ffaa00" : "#00ff88"}
                  strokeWidth="8"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 40}`}
                  strokeDashoffset={`${2 * Math.PI * 40 * (1 - currentProgress / 100)}`}
                  className="transition-all duration-300"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-white text-xs font-bold">{Math.round(currentProgress)}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  });

const handleAnswerSelect = useCallback((selectedOption) => {
  if (isAnswered || !currentQuestion) return;
  
  const responseTime = Date.now() - questionStartTime;
  const isCorrect = selectedOption === currentQuestion.correctAnswer;
  
  setSelectedAnswer(selectedOption);
  setIsAnswered(true);
  
  // Calculate the final correct count
  const finalCorrectCount = correctAnswers + (isCorrect ? 1 : 0);
  
  // Batch state updates for better performance
  if (isCorrect) {
    const speedBonus = calculateSpeedBonus(responseTime);
    const newStreak = currentStreak + 1;
    const isStreakBonus = newStreak >= 3;
    
    let points = SCORING_CONSTANTS.BASE_POINTS;
    if (isStreakBonus) {
      points *= SCORING_CONSTANTS.STREAK_MULTIPLIER;
    }
    
    // Single state update with all changes
    setCorrectAnswers(prev => prev + 1);
    setCurrentStreak(newStreak);
    setBestStreak(prev => Math.max(prev, newStreak));
    setTotalScore(prev => prev + points + speedBonus);
    setSpeedBonuses(prev => prev + speedBonus);
    if (isStreakBonus) {
      setStreakBonuses(prev => prev + (SCORING_CONSTANTS.BASE_POINTS * (SCORING_CONSTANTS.STREAK_MULTIPLIER - 1)));
    }
    
    setFeedback('correct');
    triggerCelebration(isStreakBonus);
    
    if (!isStreakBonus) {
      playCorrectSound();
    }
  } else {
    setSessionMistakes(prev => [...prev, {
      timestamp: Date.now(),
      question: currentQuestion?.arabicWord || 'Unknown',
      correctAnswer: currentQuestion?.englishNumeral || 0,
      userAnswer: selectedAnswer,
      responseTime: Date.now() - questionStartTime
    }]);
    setCurrentStreak(0);
    setFeedback('incorrect');
    playIncorrectSound();
  }
  
  // Move to next question after delay
  setTimeout(() => {
    if (currentQuestionIndex + 1 >= levelConfig.questionsCount) {
      // Pass the actual correct count to avoid race conditions
      handleLevelComplete(finalCorrectCount);
    } else {
      setCurrentQuestionIndex(prev => prev + 1);
      setupNewQuestion();
    }
  }, isCorrect ? 1200 : 2000);// Slightly reduced delays
  }, [
    isAnswered,
    currentQuestion,
    questionStartTime,
    currentStreak,
    currentQuestionIndex,
    levelConfig.questionsCount,
    calculateSpeedBonus,
    triggerCelebration,
    playCorrectSound,
    playIncorrectSound,
    setupNewQuestion
  ]);

  const handleLevelComplete = useCallback((finalCorrectAnswers = null) => {
    timer.stop();
    gameActiveRef.current = false;
    
    // Use the passed value or fall back to state (for timer-based completion)
    const actualCorrectAnswers = finalCorrectAnswers !== null ? finalCorrectAnswers : correctAnswers;
    const actualTotalQuestions = currentQuestionIndex + 1; // Total questions answered
    
    const accuracy = Math.round((actualCorrectAnswers / actualTotalQuestions) * 100);
    const accuracyBonus = accuracy === 100 ? 
      SCORING_CONSTANTS.PERFECT_ACCURACY_BONUS : 
      Math.round((accuracy / 100) * SCORING_CONSTANTS.ACCURACY_BONUS_MAX);
    
    const finalScore = totalScore + accuracyBonus + SCORING_CONSTANTS.LEVEL_COMPLETE_BONUS;
    const earnedStars = calculateStars(accuracy, finalScore, actualTotalQuestions);
    
    const scoreData = {
      totalScore: finalScore,
      accuracy,
      stars: earnedStars,
      streak: bestStreak,
      speedBonuses,
      streakBonuses,
      accuracyBonus,
      timeBonus: SCORING_CONSTANTS.LEVEL_COMPLETE_BONUS,
      completedAt: new Date().toISOString()
    };
    
    setStars(earnedStars);
    saveLevelData(level, scoreData);
    const sessionData = {
      gameType: 'arabicNumberSpeed',
      level,
      sessionStartTime,
      sessionDuration: Math.round((Date.now() - sessionStartTime) / 1000),
      totalScore: finalScore,
      accuracy: accuracy,
      attempts: actualTotalQuestions,
      correctAnswers: actualCorrectAnswers,
      mistakes: sessionMistakes,
      starsEarned: earnedStars,
      speedBonuses,
      streakBonuses,
      bestStreak,
      levelTitle: levelConfig.title
    };
    saveSessionData(sessionData);
    
    const nextLevel = level + 1;
    const isLastLevel = !LEVEL_CONFIGS[nextLevel];
    const passedLevel = accuracy >= levelConfig.passingScore;
    
    setLevelCompleteData({
      ...scoreData,
      currentLevel: level,
      nextLevel,
      isLastLevel,
      passedLevel,
      levelTitle: levelConfig.title,
      passingScore: levelConfig.passingScore
    });
    
    if (earnedStars > 0) {
      triggerCelebration(false, earnedStars);
    }
    
    playPerfectSound();
    setGamePhase('complete');
    setShowLevelComplete(true);
    
    if (passedLevel && !isLastLevel) {
      unlockNextLevel();
    }
  }, [
    timer,
    currentQuestionIndex,
    correctAnswers, // Keep this for timer-based completion
    levelConfig.passingScore,
    levelConfig.title,
    totalScore,
    bestStreak,
    speedBonuses,
    streakBonuses,
    level,
    calculateStars,
    saveLevelData,
    triggerCelebration,
    playPerfectSound,
    unlockNextLevel
  ]);

  const handleTimeUp = useCallback(() => {
    console.log('⏰ Time up called! Game active ref:', gameActiveRef.current);
    
    if (gameActiveRef.current || currentQuestionIndex > 0) {
      console.log('⏰ Triggering level completion due to timeout');
      handleLevelComplete(null); // Pass null to use state value
    }
  }, [currentQuestionIndex, handleLevelComplete]);

  const handleTimerTick = useCallback((data) => {
    const timeLeftInSeconds = Math.ceil(data.timeLeft / 1000);
    setCurrentTimeLeft(timeLeftInSeconds);
    setCurrentProgress(data.progress);
  }, []);

  const startLevel = useCallback(() => {
    console.log('🚀 Starting level with timer duration:', levelConfig.timeLimit);
    setSessionStartTime(Date.now()); // Add this line
    setSessionMistakes([]); // Reset mistakes
    
    // Reset all game state in batches for better performance
    setCurrentQuestionIndex(0);
    setTotalScore(0);
    setCorrectAnswers(0);
    setCurrentStreak(0);
    setBestStreak(0);
    setSpeedBonuses(0);
    setStreakBonuses(0);
    setStars(0);
    setLevelStartTime(Date.now());
    setCurrentTimeLeft(levelConfig.timeLimit);
    setCurrentProgress(100);
    
    setGamePhase('playing');
    gameActiveRef.current = true;
    
    const durationInMs = levelConfig.timeLimit * 1000;
    timer.start(durationInMs, handleTimeUp, handleTimerTick);
    
    setTimeout(() => {
      setupNewQuestion();
    }, 100);
  }, [levelConfig.timeLimit, timer, handleTimeUp, handleTimerTick, setupNewQuestion]);

  const goToMenu = useCallback(() => {
    timer.stop();
    gameActiveRef.current = false;
    setGamePhase('menu');
  }, [timer]);

  const restartLevel = useCallback(() => {
    timer.stop();
    gameActiveRef.current = false;
    startLevel();
  }, [timer, startLevel]);

  const handleReplayLevel = useCallback((levelToReplay) => {
    setReplayLevel(levelToReplay);
    setShowReplayConfirmation(true);
  }, []);

  const confirmReplayLevel = useCallback(() => {
    if (replayLevel) {
      updateLevel(replayLevel);
      setShowReplayConfirmation(false);
      setReplayLevel(null);
      setGamePhase('menu');
      timer.stop();
      gameActiveRef.current = false;
    }
  }, [replayLevel, updateLevel, timer]);

  const handleResetProgress = useCallback(() => {
    setShowResetConfirmation(true);
  }, []);

  const confirmResetProgress = useCallback(() => {
    resetAllProgress();
    updateLevel(1);
    setGamePhase('menu');
    timer.stop();
    gameActiveRef.current = false;
    setShowResetConfirmation(false);
  }, [resetAllProgress, updateLevel, timer]);

  const handleProceedToNextLevel = useCallback(() => {
    if (levelCompleteData?.passedLevel && !levelCompleteData?.isLastLevel) {
      updateLevel(levelCompleteData.nextLevel);
    }
    setShowLevelComplete(false);
    setLevelCompleteData(null);
    setGamePhase('menu');
    gameActiveRef.current = false;
  }, [levelCompleteData, updateLevel]);

  // Start background music
  useEffect(() => {
    const musicTimer = setTimeout(() => {
      startBackgroundMusic();
    }, 1000);
    return () => clearTimeout(musicTimer);
  }, [startBackgroundMusic]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      // Force cleanup everything
      if (audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          audioRef.current.removeEventListener('ended', audioRef.current._endHandler);
          audioRef.current.removeEventListener('error', audioRef.current._errorHandler);
          audioRef.current.src = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+Dyvmwa';
          audioRef.current.load();
        } catch (e) {
          // Ignore
        }
        audioRef.current = null;
      }
      if (audioTimeoutRef.current) {
        clearTimeout(audioTimeoutRef.current);
        audioTimeoutRef.current = null;
      }
      gameActiveRef.current = false;
      
      // Clear confetti
      confetti.reset();
    };
  }, []);

  // Memoized components for better performance
  const StarDisplay = React.memo(({ count, size = "text-2xl" }) => (
    <div className={`flex justify-center gap-1 ${size}`}>
      {[1, 2, 3].map(star => (
        <span 
          key={star} 
          className={`${star <= count ? 'text-yellow-400' : 'text-gray-300'} drop-shadow-lg transition-all duration-300 ${star <= count ? 'animate-pulse' : ''}`}
        >
          ⭐
        </span>
      ))}
    </div>
  ));

  StarDisplay.propTypes = {
    count: PropTypes.number.isRequired,
    size: PropTypes.string
  };

  // Mobile-friendly floating game stats
  const FloatingGameStats = React.memo(() => (
    <div className="fixed top-4 left-4 right-4 z-30 md:relative md:top-0 md:left-0 md:right-0">
      <div className="bg-gradient-to-r from-purple-500/90 to-pink-500/90 backdrop-blur-sm rounded-2xl p-4 shadow-2xl border-2 border-white/50">
        <div className="grid grid-cols-4 gap-2 text-center">
          <div className="bg-white/20 rounded-xl p-2 backdrop-blur-sm">
            <div className="text-lg md:text-2xl mb-1">🎯</div>
            <p className="text-white font-bold text-sm md:text-xl">{totalScore}</p>
            <p className="text-white text-xs font-bold">Score</p>
          </div>
          <div className="bg-white/20 rounded-xl p-2 backdrop-blur-sm">
            <div className="text-lg md:text-2xl mb-1">✅</div>
            <p className="text-white font-bold text-sm md:text-xl">{correctAnswers}/{levelConfig.questionsCount}</p>
            <p className="text-white text-xs font-bold">Correct</p>
          </div>
          <div className="bg-white/20 rounded-xl p-2 backdrop-blur-sm">
            <div className="text-lg md:text-2xl mb-1">🔥</div>
            <p className="text-white font-bold text-sm md:text-xl">{currentStreak}</p>
            <p className="text-white text-xs font-bold">Streak</p>
          </div>
          <div className="bg-white/20 rounded-xl p-2 backdrop-blur-sm">
            <div className="text-lg md:text-2xl mb-1">⏰</div>
            <p className={`text-white font-bold text-sm md:text-xl ${currentTimeLeft <= 10 ? 'animate-pulse text-red-200' : ''}`}>
              {Math.ceil(currentTimeLeft)}
            </p>
            <p className="text-white text-xs font-bold">Time</p>
          </div>
        </div>
        {currentStreak >= 3 && (
          <div className="mt-2 text-center bg-gradient-to-r from-orange-400/80 to-red-400/80 rounded-xl p-2">
            <p className="text-white font-bold text-sm md:text-lg animate-pulse">
              🔥 {currentStreak} in a row! ON FIRE! 🔥
            </p>
          </div>
        )}
      </div>
    </div>
  ));

  // Mobile-friendly floating controls
  const FloatingControls = React.memo(() => (
    <div className="fixed bottom-4 left-4 right-4 z-30 md:relative md:bottom-0 md:left-0 md:right-0">
      <div className="bg-gradient-to-r from-red-400/90 to-pink-500/90 backdrop-blur-sm rounded-2xl p-4 shadow-2xl border-2 border-white/50">
        <div className="flex gap-2">
          <button 
            onClick={goToMenu}
            className="flex-1 px-3 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold text-sm hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            🏠 Menu
          </button>
          <button 
            onClick={restartLevel}
            className="flex-1 px-3 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl font-bold text-sm hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            🔄 Restart
          </button>
        </div>
      </div>
    </div>
  ));

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-400 via-purple-500 to-indigo-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-sm w-full">
          <div className="text-center">
            <div className="text-4xl md:text-6xl mb-4 animate-spin">🌟</div>
            <div className="text-purple-600 text-xl md:text-3xl font-bold animate-pulse">Loading Raqam Rush...</div>
            <div className="text-gray-600 text-sm md:text-lg mt-2">Getting ready for fun!</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-400 via-purple-500 to-indigo-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-2xl">
          <div className="text-4xl md:text-6xl mb-4">😢</div>
          <h2 className="text-xl md:text-3xl font-bold text-red-600 mb-4">Oops! Something went wrong</h2>
          <p className="text-gray-700 mb-6 text-sm md:text-lg">{error?.message || 'Something went wrong'}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-2xl font-bold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            🔄 Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-400 via-purple-500 to-indigo-600 p-4">
      <AudioElements />
      
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-4 md:mb-8">
          <h1 className="text-3xl md:text-6xl font-black text-white mb-2 md:mb-4 drop-shadow-2xl">
            🚀 Raqam Rush 🚀
          </h1>
          <p className="text-lg md:text-2xl text-white font-bold opacity-90 drop-shadow-lg">
            Master Arabic numerals with speed and fun!
          </p>
        </div>

        {gamePhase === 'menu' && (
          <div className="max-w-4xl mx-auto">
            {/* Level selection and info */}
            <div className="bg-white rounded-3xl shadow-2xl p-4 md:p-8 mb-4 md:mb-8 border-4 border-yellow-300">
              <h2 className="text-2xl md:text-4xl font-bold text-center mb-2 md:mb-4 text-purple-600">
                🎮 Level {level}: {levelConfig.title}
              </h2>
              <p className="text-center text-gray-700 mb-4 md:mb-6 text-lg md:text-xl font-semibold">{levelConfig.description}</p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-4 md:mb-8">
                <div className="text-center bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl p-3 md:p-6 text-white">
                  <div className="text-3xl md:text-5xl mb-1 md:mb-3">🎯</div>
                  <p className="font-bold text-sm md:text-lg">Range</p>
                  <p className="text-lg md:text-2xl font-black">{levelConfig.range[0]} - {levelConfig.range[1]}</p>
                                 </div>
               <div className="text-center bg-gradient-to-br from-green-400 to-green-600 rounded-2xl p-3 md:p-6 text-white">
                 <div className="text-3xl md:text-5xl mb-1 md:mb-3">⏱️</div>
                 <p className="font-bold text-sm md:text-lg">Time</p>
                 <p className="text-lg md:text-2xl font-black">{levelConfig.timeLimit}s</p>
               </div>
               <div className="text-center bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl p-3 md:p-6 text-white">
                 <div className="text-3xl md:text-5xl mb-1 md:mb-3">❓</div>
                 <p className="font-bold text-sm md:text-lg">Questions</p>
                 <p className="text-lg md:text-2xl font-black">{levelConfig.questionsCount}</p>
               </div>
               <div className="text-center bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl p-3 md:p-6 text-white">
                 <div className="text-3xl md:text-5xl mb-1 md:mb-3">🏆</div>
                 <p className="font-bold text-sm md:text-lg">Pass</p>
                 <p className="text-lg md:text-2xl font-black">{levelConfig.passingScore}%</p>
               </div>
             </div>

             <div className="text-center">
               <button
                 onClick={startLevel}
                 className="px-8 md:px-12 py-4 md:py-6 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-3xl font-black text-xl md:text-3xl hover:shadow-2xl transform hover:scale-110 transition-all duration-300 border-4 border-white"
               >
                 🚀 START LEVEL {level}! 🚀
               </button>
             </div>
           </div>

           {/* Level grid - mobile optimized */}
           <div className="bg-white rounded-3xl shadow-2xl p-4 md:p-8 border-4 border-pink-300">
             <h3 className="text-xl md:text-3xl font-bold text-center mb-4 md:mb-6 text-purple-600">🎮 All Amazing Levels!</h3>
             <div className="grid grid-cols-4 md:grid-cols-7 gap-2 md:gap-4">
               {Object.entries(LEVEL_CONFIGS).map(([levelNum, config]) => {
                 const num = parseInt(levelNum);
                 const isUnlocked = num <= maxLevel;
                 const isCurrent = num === level;
                 const levelScore = bestScores[`level${num}`];
                 
                 return (
                   <button
                     key={num}
                     onClick={() => isUnlocked && !isCurrent ? handleReplayLevel(num) : updateLevel(num)}
                     disabled={!isUnlocked}
                     className={`
                       relative p-2 md:p-4 rounded-2xl text-sm md:text-lg font-bold transition-all duration-300 min-h-[80px] md:min-h-[120px] flex flex-col items-center justify-center border-2 md:border-4
                       ${isCurrent 
                         ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-2xl transform scale-105 md:scale-110 border-yellow-300' 
                         : isUnlocked 
                           ? 'bg-gradient-to-br from-green-100 to-green-200 text-green-800 hover:from-green-200 hover:to-green-300 cursor-pointer hover:scale-105 border-green-300' 
                           : 'bg-gray-200 text-gray-400 cursor-not-allowed border-gray-300'
                       }
                     `}
                     title={isUnlocked ? config.title : 'Locked'}
                   >
                     <div className="text-lg md:text-2xl font-black">{num}</div>
                     <div className="text-xs mt-1 font-bold hidden md:block">{config.range[0]}-{config.range[1]}</div>
                     {levelScore && (
                       <div className="absolute -top-1 -right-1 md:-top-2 md:-right-2">
                         <StarDisplay count={levelScore.stars || 0} size="text-xs md:text-sm" />
                       </div>
                     )}
                     {!isUnlocked && (
                       <div className="absolute inset-0 flex items-center justify-center">
                         <span className="text-xl md:text-3xl">🔒</span>
                       </div>
                     )}
                   </button>
                 );
               })}
             </div>
             
             <div className="mt-4 md:mt-8 flex gap-2 md:gap-4 justify-center flex-wrap">
              <Link to="/">
                <button 
                  className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  🔙 Main Menu
                </button>
              </Link>
               <button 
                 onClick={handleResetProgress}
                 className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
               >
                 🗑️ Reset Progress
               </button>
               <button 
                 onClick={() => setShowInstructions(true)}
                 className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
               >
                 📖 How to Play
               </button>
               <button 
                 onClick={() => setShowScoringHelp(true)}
                 className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
               >
                 🏆 Scoring System
               </button>
             </div>
           </div>
         </div>
       )}

       {gamePhase === 'playing' && (
         <div className="relative">
           {/* Floating stats and controls for mobile, integrated for desktop */}
          <div className="md:hidden">
            {/* Timer - top center */}
            <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-30">
              <div className="scale-75">
                <BigTimer />
              </div>
            </div>
            
            {/* Stats - top sides */}
            <div className="fixed top-4 left-4 right-4 z-30 mt-20">
              <FloatingGameStats />
            </div>
            
            {/* Controls - bottom */}
            <FloatingControls />
          </div>
           
        {/* Desktop sidebar layout */}
        <div className="hidden md:grid md:grid-cols-4 gap-6">
          {/* Left Sidebar - Timer, Stats and Controls */}
          <div className="space-y-6">
            {/* Timer Box */}
            <BigTimer />
            
            {/* Stats Box */}
            <div className="bg-gradient-to-br from-blue-400 to-purple-500 rounded-2xl shadow-xl p-6 border-4 border-white">
              <h3 className="text-2xl font-bold text-center mb-4 text-white">📊 Your Stats!</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white bg-opacity-20 rounded-xl p-4 text-center backdrop-blur-sm">
                  <div className="text-3xl mb-2">🎯</div>
                  <p className="text-white font-bold text-xl">{totalScore}</p>
                  <p className="text-white text-xs font-bold">Score</p>
                </div>
                <div className="bg-white bg-opacity-20 rounded-xl p-4 text-center backdrop-blur-sm">
                  <div className="text-3xl mb-2">✅</div>
                  <p className="text-white font-bold text-xl">{correctAnswers}/{levelConfig.questionsCount}</p>
                  <p className="text-white text-xs font-bold">Correct</p>
                </div>
                <div className="bg-white bg-opacity-20 rounded-xl p-4 text-center backdrop-blur-sm">
                  <div className="text-3xl mb-2">🔥</div>
                  <p className="text-white font-bold text-xl">{currentStreak}</p>
                  <p className="text-white text-xs font-bold">Streak</p>
                </div>
                <div className="bg-white bg-opacity-20 rounded-xl p-4 text-center backdrop-blur-sm">
                  <div className="text-3xl mb-2">🏆</div>
                  <p className="text-white font-bold text-xl">{bestStreak}</p>
                  <p className="text-white text-xs font-bold">Best</p>
                </div>
              </div>
              {currentStreak >= 3 && (
                <div className="mt-4 text-center bg-gradient-to-r from-orange-400 to-red-400 rounded-xl p-3">
                  <p className="text-white font-bold text-lg animate-pulse">
                    🔥 {currentStreak} in a row! ON FIRE! 🔥
                  </p>
                </div>
              )}
            </div>

            {/* Controls Box */}
            <div className="bg-gradient-to-br from-red-400 to-pink-500 rounded-2xl shadow-xl p-6 border-4 border-white">
              <h3 className="text-2xl font-bold text-center mb-4 text-white">🎮 Controls</h3>
              <div className="space-y-3">
                <button 
                  onClick={goToMenu}
                  className="w-full px-6 py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl font-bold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  🏠 Back to Menu
                </button>
                <button 
                  onClick={restartLevel}
                  className="w-full px-6 py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-2xl font-bold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  🔄 Restart Level
                </button>
              </div>
            </div>
          </div>

             {/* Main Game Area */}
             <div className="col-span-3">
               <div className="bg-white rounded-3xl shadow-2xl p-8 border-4 border-yellow-300 min-h-[600px]">
                 {currentQuestion ? (
                   <div className="text-center">
                     {/* Progress indicator */}
                     <div className="mb-8">
                       <p className="text-2xl text-gray-600 mb-4 font-bold">
                         Question {currentQuestionIndex + 1} of {levelConfig.questionsCount}
                       </p>
                       <div className="w-full bg-gray-200 rounded-full h-6 border-2 border-gray-300">
                         <div 
                           className="bg-gradient-to-r from-purple-500 to-pink-500 h-full rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                           style={{ width: `${((currentQuestionIndex + 1) / levelConfig.questionsCount) * 100}%` }}
                         >
                           <span className="text-white text-xs font-bold">
                             {Math.round(((currentQuestionIndex + 1) / levelConfig.questionsCount) * 100)}%
                           </span>
                         </div>
                       </div>
                     </div>

                     {/* Number display */}
                     <div className="mb-10">
                       <div className="text-9xl font-black text-purple-600 mb-6 animate-pulse drop-shadow-2xl">
                         {currentQuestion.arabicNumeral}
                       </div>
                       <div className="text-3xl text-gray-700 mb-6 font-bold">
                         {currentQuestion.arabicWord}
                       </div>
                       <button
                         onClick={() => playNumberAudio(currentQuestion.number)}
                         disabled={audioPlaying}
                         className={`px-8 py-4 rounded-2xl font-bold text-xl transition-all duration-200 border-4 ${
                           audioPlaying 
                             ? 'bg-gray-400 text-gray-600 cursor-not-allowed border-gray-300' 
                             : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:shadow-2xl transform hover:scale-110 border-white'
                         }`}
                       >
                         {audioPlaying ? '🔊 Playing...' : '🔊 Listen Again'}
                       </button>
                     </div>

                     {/* Question prompt */}
                     <div className="mb-8">
                       <h2 className="text-3xl font-black text-gray-800 mb-3">
                         What number is this? 🤔
                       </h2>
                       <p className="text-gray-600 text-xl font-semibold">Choose the correct English number!</p>
                     </div>

                     {/* Answer options */}
                     <div className="grid grid-cols-2 gap-6 mb-8">
                       {answerOptions.map((option, index) => {
                         let buttonClass = "p-8 rounded-2xl font-black text-3xl transition-all duration-200 border-4 ";
                         
                         if (!isAnswered) {
                           buttonClass += "bg-gradient-to-br from-gray-50 to-gray-100 border-gray-300 hover:from-blue-50 hover:to-blue-100 hover:border-blue-400 cursor-pointer transform hover:scale-110 hover:shadow-2xl";
                         } else {
                           if (option === currentQuestion.correctAnswer) {
                             buttonClass += "bg-gradient-to-br from-green-400 to-green-500 border-green-600 text-white animate-pulse";
                           } else if (option === selectedAnswer) {
                             buttonClass += "bg-gradient-to-br from-red-400 to-red-500 border-red-600 text-white";
                           } else {
                             buttonClass += "bg-gray-200 border-gray-300 text-gray-600";
                           }
                         }
                         
                         return (
                           <button
                             key={`${option}-${index}`}
                             onClick={() => handleAnswerSelect(option)}
                             disabled={isAnswered}
                             className={buttonClass}
                           >
                             {option}
                           </button>
                         );
                       })}
                     </div>

                     {/* Feedback */}
                     {feedback && (
                       <div className={`text-2xl font-bold mb-6 p-4 rounded-2xl ${
                         feedback === 'correct' 
                           ? 'text-green-600 bg-green-100 border-4 border-green-300' 
                           : 'text-red-600 bg-red-100 border-4 border-red-300'
                       }`}>
                         {feedback === 'correct' ? 
                           '✅ Amazing! You got it right! 🎉' : 
                           `❌ Oops! The answer was ${currentQuestion.correctAnswer} ☹️`
                         }
                       </div>
                     )}
                   </div>
                 ) : (
                   <div className="text-center">
                     <div className="text-8xl mb-6 animate-spin">⏳</div>
                     <p className="text-2xl text-gray-600 font-bold">Getting your next question ready...</p>
                   </div>
                 )}
               </div>
             </div>
           </div>

           {/* Mobile-only main game area */}
           <div className="md:hidden mt-20 mb-20">
             <div className="bg-white rounded-3xl shadow-2xl p-6 border-4 border-yellow-300 min-h-[500px]">
               {currentQuestion ? (
                 <div className="text-center">
                   {/* Progress indicator */}
                   <div className="mb-6">
                     <p className="text-lg text-gray-600 mb-3 font-bold">
                       Question {currentQuestionIndex + 1} of {levelConfig.questionsCount}
                     </p>
                     <div className="w-full bg-gray-200 rounded-full h-4 border-2 border-gray-300">
                       <div 
                         className="bg-gradient-to-r from-purple-500 to-pink-500 h-full rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                         style={{ width: `${((currentQuestionIndex + 1) / levelConfig.questionsCount) * 100}%` }}
                       >
                         <span className="text-white text-xs font-bold">
                           {Math.round(((currentQuestionIndex + 1) / levelConfig.questionsCount) * 100)}%
                         </span>
                       </div>
                     </div>
                   </div>

                   {/* Number display - mobile optimized */}
                   <div className="mb-8">
                     <div className="text-6xl font-black text-purple-600 mb-4 animate-pulse drop-shadow-2xl">
                       {currentQuestion.arabicNumeral}
                     </div>
                     <div className="text-xl text-gray-700 mb-4 font-bold">
                       {currentQuestion.arabicWord}
                     </div>
                     <button
                       onClick={() => playNumberAudio(currentQuestion.number)}
                       disabled={audioPlaying}
                       className={`px-6 py-3 rounded-2xl font-bold text-lg transition-all duration-200 border-4 ${
                         audioPlaying 
                           ? 'bg-gray-400 text-gray-600 cursor-not-allowed border-gray-300' 
                           : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:shadow-2xl transform hover:scale-110 border-white'
                       }`}
                     >
                       {audioPlaying ? '🔊 Playing...' : '🔊 Listen Again'}
                     </button>
                   </div>

                   {/* Question prompt */}
                   <div className="mb-6">
                     <h2 className="text-2xl font-black text-gray-800 mb-2">
                       What number is this? 🤔
                     </h2>
                     <p className="text-gray-600 text-lg font-semibold">Choose the correct English number!</p>
                   </div>

                   {/* Answer options - mobile optimized */}
                   <div className="grid grid-cols-2 gap-4 mb-6">
                     {answerOptions.map((option, index) => {
                       let buttonClass = "p-6 rounded-2xl font-black text-2xl transition-all duration-200 border-4 min-h-[80px] flex items-center justify-center ";
                       
                       if (!isAnswered) {
                         buttonClass += "bg-gradient-to-br from-gray-50 to-gray-100 border-gray-300 hover:from-blue-50 hover:to-blue-100 hover:border-blue-400 cursor-pointer transform hover:scale-105 hover:shadow-2xl active:scale-95";
                       } else {
                         if (option === currentQuestion.correctAnswer) {
                           buttonClass += "bg-gradient-to-br from-green-400 to-green-500 border-green-600 text-white animate-pulse";
                         } else if (option === selectedAnswer) {
                           buttonClass += "bg-gradient-to-br from-red-400 to-red-500 border-red-600 text-white";
                         } else {
                           buttonClass += "bg-gray-200 border-gray-300 text-gray-600";
                         }
                       }
                       
                       return (
                         <button
                           key={`${option}-${index}`}
                           onClick={() => handleAnswerSelect(option)}
                           disabled={isAnswered}
                           className={buttonClass}
                         >
                           {option}
                         </button>
                       );
                     })}
                   </div>

                   {/* Feedback */}
                   {feedback && (
                     <div className={`text-lg font-bold mb-4 p-3 rounded-2xl ${
                       feedback === 'correct' 
                         ? 'text-green-600 bg-green-100 border-4 border-green-300' 
                         : 'text-red-600 bg-red-100 border-4 border-red-300'
                     }`}>
                       {feedback === 'correct' ? 
                         '✅ Amazing! You got it right! 🎉' : 
                         `❌ Oops! The answer was ${currentQuestion.correctAnswer} ☹️`
                       }
                     </div>
                   )}
                 </div>
               ) : (
                 <div className="text-center">
                   <div className="text-6xl mb-4 animate-spin">⏳</div>
                   <p className="text-lg text-gray-600 font-bold">Getting your next question ready...</p>
                 </div>
               )}
             </div>
           </div>
         </div>
       )}

       {/* Celebration Effects */}
       {celebrations && (
         <div className="fixed inset-0 pointer-events-none z-40">
           <div className="absolute inset-0 flex items-center justify-center">
             <div className="text-6xl md:text-9xl animate-bounce">🎉</div>
           </div>
         </div>
       )}

       {sparkles && (
         <div className="fixed inset-0 pointer-events-none z-40">
           {[...Array(20)].map((_, i) => (
             <div
               key={`sparkle-${i}`}
               className="absolute animate-ping text-yellow-400 text-2xl md:text-3xl"
               style={{
                 left: `${Math.random() * 100}%`,
                 top: `${Math.random() * 100}%`,
                 animationDelay: `${Math.random() * 2}s`
               }}
             >
               ✨
             </div>
           ))}
         </div>
       )}
     </div>

     {/* Modals - mobile optimized */}
     {showInstructions && (
       <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
         <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border-4 border-purple-300">
           <div className="p-4 md:p-8">
             <div className="flex justify-between items-center mb-4 md:mb-6">
               <h2 className="text-2xl md:text-3xl font-bold text-purple-600">📖 How to Play!</h2>
               <button 
                 onClick={() => setShowInstructions(false)}
                 className="text-gray-500 hover:text-gray-700 text-2xl md:text-3xl font-bold w-10 h-10 flex items-center justify-center"
               >
                 ×
               </button>
             </div>
             
             <div className="space-y-4 md:space-y-6">
               <div className="bg-gradient-to-r from-blue-100 to-blue-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-blue-300">
                 <h3 className="font-bold text-blue-800 mb-2 md:mb-3 text-lg md:text-2xl">🎯 Your Mission</h3>
                 <p className="text-blue-700 text-sm md:text-lg">Listen to Arabic numbers and quickly pick the correct English number. Be fast and accurate to get high scores!</p>
               </div>
               
               <div className="bg-gradient-to-r from-green-100 to-green-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-green-300">
                 <h3 className="font-bold text-green-800 mb-2 md:mb-3 text-lg md:text-2xl">🎮 How to Play</h3>
                 <ul className="list-disc list-inside text-green-700 space-y-1 md:space-y-2 text-sm md:text-lg">
                   <li>You'll see an Arabic number and hear how to say it</li>
                   <li>Pick the correct English number from 4 choices</li>
                   <li>Answer super fast to get speed bonuses!</li>
                   <li>Get 3+ correct in a row for amazing streak bonuses</li>
                   <li>Beat the clock to complete the level!</li>
                 </ul>
               </div>
               
               <div className="bg-gradient-to-r from-yellow-100 to-yellow-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-yellow-300">
                 <h3 className="font-bold text-yellow-800 mb-2 md:mb-3 text-lg md:text-2xl">⏱️ Time Challenge</h3>
                 <p className="text-yellow-700 text-sm md:text-lg">Each level has a time limit. Answer quickly to earn speed bonuses, but don't rush and make mistakes!</p>
               </div>
               
               <div className="bg-gradient-to-r from-purple-100 to-purple-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-purple-300">
                 <h3 className="font-bold text-purple-800 mb-2 md:mb-3 text-lg md:text-2xl">🏆 Level Up</h3>
                 <p className="text-purple-700 text-sm md:text-lg">Get the passing score to unlock the next level. Each level has bigger numbers and more challenges!</p>
               </div>
               
               <div className="bg-gradient-to-r from-red-100 to-red-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-red-300">
                 <h3 className="font-bold text-red-800 mb-2 md:mb-3 text-lg md:text-2xl">💡 Pro Tips</h3>
                 <ul className="list-disc list-inside text-red-700 space-y-1 md:space-y-2 text-sm md:text-lg">
                   <li>Listen carefully to how the number sounds</li>
                   <li>Look for patterns in Arabic numbers</li>
                   <li>Keep your streak going for bonus points</li>
                   <li>Balance speed with accuracy</li>
                   <li>Use the "Listen Again" button if you need to</li>
                 </ul>
               </div>
             </div>
             
             <div className="mt-4 md:mt-8 text-center">
               <button 
                 onClick={() => setShowInstructions(false)}
                 className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-bold text-lg md:text-xl"
               >
                 Got it! Let's Play! 🚀
               </button>
             </div>
           </div>
         </div>
       </div>
     )}

     {showScoringHelp && (
       <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
         <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border-4 border-yellow-300">
           <div className="p-4 md:p-8">
             <div className="flex justify-between items-center mb-4 md:mb-6">
               <h2 className="text-2xl md:text-3xl font-bold text-purple-600">🏆 How to Get High Scores!</h2>
               <button 
                 onClick={() => setShowScoringHelp(false)}
                 className="text-gray-500 hover:text-gray-700 text-2xl md:text-3xl font-bold w-10 h-10 flex items-center justify-center"
               >
                 ×
               </button>
             </div>
             
             <div className="space-y-4 md:space-y-6">
               <div className="bg-gradient-to-r from-blue-100 to-blue-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-blue-300">
                 <h3 className="font-bold text-blue-800 mb-2 md:mb-3 text-lg md:text-2xl">📊 Base Points</h3>
                 <p className="text-blue-700 text-sm md:text-lg">Get <strong>{SCORING_CONSTANTS.BASE_POINTS} points</strong> for every correct answer!</p>
               </div>
               
               <div className="bg-gradient-to-r from-green-100 to-green-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-green-300">
                 <h3 className="font-bold text-green-800 mb-2 md:mb-3 text-lg md:text-2xl">⚡ Speed Bonus</h3>
                 <p className="text-green-700 text-sm md:text-lg">Answer in <strong>{SCORING_CONSTANTS.MAX_SPEED_BONUS_TIME} seconds</strong> to get up to <strong>{SCORING_CONSTANTS.SPEED_BONUS_MAX} bonus points</strong>. Faster = more points!</p>
               </div>
               
               <div className="bg-gradient-to-r from-orange-100 to-orange-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-orange-300">
                 <h3 className="font-bold text-orange-800 mb-2 md:mb-3 text-lg md:text-2xl">🔥 Streak Power</h3>
                                  <p className="text-orange-700 text-sm md:text-lg">Get 3+ correct answers in a row to earn <strong>DOUBLE POINTS</strong> for each answer in your streak!</p>
               </div>
               
               <div className="bg-gradient-to-r from-purple-100 to-purple-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-purple-300">
                 <h3 className="font-bold text-purple-800 mb-2 md:mb-3 text-lg md:text-2xl">🎯 Accuracy Rewards</h3>
                 <p className="text-purple-700 text-sm md:text-lg">End-of-level bonuses for being accurate:</p>
                 <ul className="list-disc list-inside mt-2 md:mt-3 text-purple-600 text-sm md:text-lg">
                   <li>100% accuracy: <strong>{SCORING_CONSTANTS.PERFECT_ACCURACY_BONUS} points</strong></li>
                   <li>Other accuracies: Up to <strong>{SCORING_CONSTANTS.ACCURACY_BONUS_MAX} points</strong></li>
                 </ul>
               </div>
               
               <div className="bg-gradient-to-r from-yellow-100 to-yellow-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-yellow-300">
                 <h3 className="font-bold text-yellow-800 mb-2 md:mb-3 text-lg md:text-2xl">⭐ Star Ratings</h3>
                 <p className="text-yellow-700 text-sm md:text-lg">Earn stars based on your amazing performance:</p>
                 <ul className="list-disc list-inside mt-2 md:mt-3 text-yellow-600 text-sm md:text-lg">
                   <li><span className="text-yellow-400">⭐⭐⭐</span> AMAZING! (95%+ accuracy + high score)</li>
                   <li><span className="text-yellow-400">⭐⭐</span> Great! (85%+ accuracy + good score)</li>
                   <li><span className="text-yellow-400">⭐</span> Good! (70%+ accuracy + decent score)</li>
                 </ul>
               </div>
               
               <div className="bg-gradient-to-r from-red-100 to-red-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-red-300">
                 <h3 className="font-bold text-red-800 mb-2 md:mb-3 text-lg md:text-2xl">🏁 Completion Bonus</h3>
                 <p className="text-red-700 text-sm md:text-lg">Finish all questions to earn a <strong>{SCORING_CONSTANTS.LEVEL_COMPLETE_BONUS} point completion bonus</strong>!</p>
               </div>
             </div>
             
             <div className="mt-4 md:mt-8 text-center">
               <button 
                 onClick={() => setShowScoringHelp(false)}
                 className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-bold text-lg md:text-xl"
               >
                 Ready to Score Big! 🎯
               </button>
             </div>
           </div>
         </div>
       </div>
     )}

     {showLevelComplete && levelCompleteData && (
       <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
         <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-4 md:p-8 border-4 border-yellow-300">
           <div className="text-center">
             <div className="text-6xl md:text-8xl mb-4 md:mb-6">
               {levelCompleteData.passedLevel ? '🎉' : '😔'}
             </div>
             <h2 className="text-2xl md:text-3xl font-bold text-purple-600 mb-2 md:mb-3">
               Level {levelCompleteData.currentLevel} Complete!
             </h2>
             <h3 className="text-lg md:text-xl text-gray-700 mb-4 md:mb-6 font-semibold">
               {levelCompleteData.levelTitle}
             </h3>
             
             <div className="mb-6 md:mb-8">
               <StarDisplay count={levelCompleteData.stars} size="text-3xl md:text-4xl" />
               <p className="text-xl md:text-2xl font-bold text-indigo-600 mt-3 md:mt-4">
                 Final Score: {levelCompleteData.totalScore}
               </p>
               <p className="text-base md:text-lg text-gray-600 mt-2">
                 Accuracy: {levelCompleteData.accuracy}% • Best Streak: {levelCompleteData.streak}
               </p>
             </div>

             <div className="mb-6 md:mb-8">
               {levelCompleteData.passedLevel ? (
                 <div className="bg-gradient-to-r from-green-100 to-green-200 border-2 md:border-4 border-green-400 rounded-2xl p-4 md:p-6">
                   <p className="text-green-800 font-bold text-lg md:text-xl">
                     ✅ You PASSED! Amazing! 🎉
                   </p>
                   <p className="text-green-700 text-base md:text-lg">
                     {levelCompleteData.accuracy}% ≥ {levelCompleteData.passingScore}%
                   </p>
                   {!levelCompleteData.isLastLevel && (
                     <p className="text-green-700 text-base md:text-lg mt-2">Level {levelCompleteData.nextLevel} is now unlocked!</p>
                   )}
                 </div>
               ) : (
                 <div className="bg-gradient-to-r from-red-100 to-red-200 border-2 md:border-4 border-red-400 rounded-2xl p-4 md:p-6">
                   <p className="text-red-800 font-bold text-lg md:text-xl">
                     ☹️ Almost there! Keep trying!
                   </p>
                   <p className="text-red-700 text-base md:text-lg">
                     You need {levelCompleteData.passingScore}% to pass
                   </p>
                   <p className="text-red-700 text-base md:text-lg">Try again to unlock the next level!</p>
                 </div>
               )}
             </div>

             <div className="space-y-3 md:space-y-4">
               <button 
                 onClick={() => { 
                   setShowLevelComplete(false); 
                   setGamePhase('menu'); 
                   gameActiveRef.current = false;
                 }}
                 className="w-full px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-xl transform hover:scale-105 transition-all duration-200"
               >
                 🔄 Play Again
               </button>
               
               {levelCompleteData.passedLevel && !levelCompleteData.isLastLevel && (
                 <button 
                   onClick={handleProceedToNextLevel}
                   className="w-full px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-2xl transform hover:scale-110 transition-all duration-300"
                 >
                   🚀 Next Level! 🚀
                 </button>
               )}
               
               {levelCompleteData.isLastLevel && (
                 <button 
                   onClick={() => { 
                     setShowLevelComplete(false); 
                     setGamePhase('menu'); 
                     gameActiveRef.current = false;
                   }}
                   className="w-full px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-2xl transform hover:scale-110 transition-all duration-300"
                 >
                   🎊 You're a Champion! 🎊
                 </button>
               )}
             </div>
           </div>
         </div>
       </div>
     )}

     {showResetConfirmation && (
       <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
         <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-4 md:p-8 border-4 border-red-300">
           <div className="text-center">
             <div className="text-6xl md:text-8xl mb-4 md:mb-6">⚠️</div>
             <h2 className="text-2xl md:text-3xl font-bold text-red-600 mb-3 md:mb-4">Reset Everything?</h2>
             <p className="text-gray-700 mb-6 md:mb-8 text-sm md:text-lg">
               This will delete all your amazing scores, stars, and progress. You'll start from Level 1 again.
               This cannot be undone!
             </p>
             <div className="flex gap-3 md:gap-4 justify-center">
               <button 
                 onClick={() => setShowResetConfirmation(false)}
                 className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
               >
                 Cancel
               </button>
               <button 
                 onClick={confirmResetProgress}
                 className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
               >
                 Yes, Reset Everything
               </button>
             </div>
           </div>
         </div>
       </div>
     )}

     {showReplayConfirmation && (
       <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
         <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-4 md:p-8 border-4 border-blue-300">
           <div className="text-center">
             <div className="text-6xl md:text-8xl mb-4 md:mb-6">🔄</div>
             <h2 className="text-2xl md:text-3xl font-bold text-blue-600 mb-3 md:mb-4">Replay Level {replayLevel}?</h2>
             <p className="text-gray-700 mb-6 md:mb-8 text-sm md:text-lg">
               You'll restart this level and try to beat your previous score. Your best score will be kept for comparison.
             </p>
             <div className="flex gap-3 md:gap-4 justify-center">
               <button 
                 onClick={() => setShowReplayConfirmation(false)}
                 className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
               >
                 Cancel
               </button>
               <button 
                 onClick={confirmReplayLevel}
                 className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
               >
                 Yes, Let's Play!
               </button>
             </div>
           </div>
         </div>
       </div>
     )}
   </div>
 );
};

export default ArabicNumberSpeedGame;